import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Flag, CheckCircle2, Clock, ExternalLink, AlertTriangle } from "lucide-react";

const TONE = {
  pending:  "bg-amber-500/10   text-amber-300   border-amber-500/30",
  resolved: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  dismissed:"bg-stone-800/60   text-stone-300   border-stone-700",
};

export default function AdminReports() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/admin/reports");
      setRows(Array.isArray(data.reports) ? data.reports : []);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      setError({ status, detail: typeof detail === "string" ? detail : String(detail || e.message || "Failed to load reports") });
      // eslint-disable-next-line no-console
      console.warn("[AdminReports] load:", status, detail);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolve = async (rid, action) => {
    try {
      await api.post(`/admin/reports/${rid}/resolve`, { action });
      toast.success("Report resolved");
      load();
    } catch (e) {
      console.warn("[AdminReports] resolve:", e);
      toast.error("Action failed");
    }
  };

  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.22em] text-amber-400">Trust &amp; safety</p>
      <h1 className="text-2xl sm:text-3xl font-serif text-stone-50 mt-1">Reports</h1>
      <p className="text-sm text-stone-400 mt-1">{rows.length} report{rows.length === 1 ? "" : "s"}</p>

      {error && (
        <div className="mt-4 p-3 rounded-lg border border-rose-500/40 bg-rose-500/5 text-xs text-rose-200 flex items-start gap-2" data-testid="adm-reports-error">
          <AlertTriangle className="w-4 h-4 flex-none mt-0.5" />
          <div>
            <p className="font-medium">Could not load reports (HTTP {error.status ?? "?"})</p>
            <p className="text-rose-300/80 mt-0.5">{error.detail}</p>
          </div>
        </div>
      )}
      {loading && (<p className="mt-4 text-sm text-stone-400">Loading…</p>)}

      <div className="mt-5 space-y-3" data-testid="adm-reports-list">
        {rows.map(r => (
          <div key={r.id} className="rounded-xl border border-stone-800 bg-stone-950/50 p-4" data-testid={`adm-report-${r.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-stone-100 flex items-center gap-1.5"><Flag className="w-3.5 h-3.5 text-rose-400" /> {r.reason || "Unspecified"}</p>
                <div className="mt-1 flex flex-wrap gap-3 text-[11px] text-stone-500">
                  {r.target_profile_id && (
                    <Link to={`/profile/${r.target_profile_id}`} target="_blank" className="inline-flex items-center gap-1 hover:text-amber-300">
                      Profile {r.target_profile_id.slice(0, 8)}… <ExternalLink className="w-3 h-3" />
                    </Link>
                  )}
                  {r.reporter_id && <span>Reporter {r.reporter_id.slice(0, 8)}…</span>}
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</span>
                </div>
                {r.description && (
                  <p className="mt-2 p-2 text-xs text-stone-300 rounded bg-stone-900/50 border border-stone-800">{r.description}</p>
                )}
                {r.action_taken && (
                  <p className="mt-2 text-[11px] text-emerald-300">Action taken: {r.action_taken}</p>
                )}
              </div>
              <div className="flex items-start gap-2">
                <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] ${TONE[r.status] || TONE.pending}`}>{r.status || "pending"}</span>
                {r.status === "pending" && (
                  <div className="flex gap-1">
                    <button onClick={() => resolve(r.id, "resolved")}  className="px-2 py-1 rounded border border-emerald-500/30 text-emerald-300 text-xs inline-flex items-center gap-1" data-testid={`adm-report-resolve-${r.id}`}>
                      <CheckCircle2 className="w-3 h-3" /> Resolve
                    </button>
                    <button onClick={() => resolve(r.id, "dismissed")} className="px-2 py-1 rounded border border-stone-700 text-stone-300 text-xs" data-testid={`adm-report-dismiss-${r.id}`}>
                      Dismiss
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <p className="text-sm text-stone-500 text-center p-8">No reports.</p>
        )}
      </div>
    </div>
  );
}
